import React, { useCallback, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { saveAs } from 'file-saver';
import { Banner } from '@repo/design-system';
import {
  ASSET_CSV_ADOPTION_TEMPLATE_FILENAME,
  ASSET_CSV_TEMPLATE_FILENAME,
  MIDDLEWARE_REQUEST_TIMEOUT_MS,
  TIMED_OUT_MESSAGE,
  downloadAssetCsvTemplate,
  getTokenBounded,
} from '@repo/front-end-services';
import type { AssetCsvTemplateOptions } from '@repo/front-end-services';

import Config from '../../common/Config';

/**
 * Fetch and save the register template — the ONE implementation behind both of the Equipment
 * step's template buttons (`equipment-page-start`, R10): *Download the template* on a register with
 * rows (`AssetCsvTemplateButton`) and *Download Template* in the empty register's drop zone.
 *
 * Moved here unchanged from `AssetCsvTemplateButton`, so the two buttons cannot drift apart.
 *
 * ## Both waits are bounded
 *
 * ONE 30-second deadline (`MIDDLEWARE_REQUEST_TIMEOUT_MS`) is spent across both: the token fetch
 * through `getTokenBounded`, the request through `downloadAssetCsvTemplate`'s own
 * `AbortController`, each given what is left of it rather than a fresh 30 s. `auth0-spa-js`
 * serialises `getTokenSilently` across tabs on a browser lock, so the token step is the likelier
 * of the two to strand — which is the documented reason `getTokenBounded` exists. No retry: the
 * person presses again, and the read is safe to repeat.
 */
export interface AssetCsvTemplateDownload {
  /**
   * Fetch and save a template: the customer's, or with `{ mode: 'adopt', twinId }` the adoption one
   * (`equipment-page-adoption`, R12), saved as `twinlabs-adoption-template.csv`.
   */
  download: (options?: AssetCsvTemplateOptions) => Promise<void>;
  /** True while the token and the request are in flight. Show it as `loading`, never `disabled`. */
  downloading: boolean;
  /** The last attempt's failure, or null. */
  error: string | null;
  /**
   * Forget the last failure. The empty register's start screen calls it when it comes back, so a
   * failure from minutes earlier is not shown again as a current one (review).
   */
  reset: () => void;
}

export const useAssetCsvTemplateDownload = (): AssetCsvTemplateDownload => {
  const { getAccessTokenSilently } = useAuth0();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = useCallback(
    async (options: AssetCsvTemplateOptions = {}) => {
      setDownloading(true);
      setError(null);
      // ONE deadline spent across BOTH waits, not one each. Bounding them separately makes the
      // operator's worst case 30 s of sign-in plus 30 s of request — a minute of "Preparing…" on
      // the screen all 14 migrations go through — while the spec and the standards both describe a
      // 30-second total. Auth0's cross-tab lock makes a slow token the normal case rather than the
      // exotic one, so this is the arm that actually spends the budget. (Reliability reviewer.)
      const deadline = Date.now() + MIDDLEWARE_REQUEST_TIMEOUT_MS;
      try {
        // Fetched with the bearer token rather than linked: the route is TL_ADMIN-guarded like
        // the rest of the register API, so a plain href would save a 401 as a .csv.
        const accessToken = await getTokenBounded(getAccessTokenSilently, deadline - Date.now());
        const remaining = deadline - Date.now();
        if (remaining <= 0) {
          // Refuse rather than start a request with no budget left: a fetch issued here would
          // either be aborted immediately or run unbounded, and neither is an honest answer.
          throw Object.assign(new Error(TIMED_OUT_MESSAGE), { name: 'TimeoutError' });
        }
        // `file-saver`, the app's one way to save a file (`equipment-page-upload-values`, R13a).
        saveAs(
          await downloadAssetCsvTemplate(Config.middlewareApi, accessToken, remaining, options),
          options.mode === 'adopt'
            ? ASSET_CSV_ADOPTION_TEMPLATE_FILENAME
            : ASSET_CSV_TEMPLATE_FILENAME,
        );
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Could not download the template. Please try again.',
        );
      } finally {
        setDownloading(false);
      }
    },
    [getAccessTokenSilently],
  );

  const reset = useCallback(() => setError(null), []);

  return { download, downloading, error, reset };
};

/**
 * The download's failure, announced. ONE live region, and it is this wrapper — rendered
 * UNCONDITIONALLY, because a live region inserted together with its content is routinely missed.
 *
 * Polite: the operator pressed a button and is waiting for its result. The Banner is given
 * `role="status"` because it defaults a `tone="error"` to `role="alert"`, which is implicitly
 * ASSERTIVE and would override this region. (Frontend/a11y reviewer; GitHub Copilot, PR #1686.)
 */
export const AssetCsvTemplateDownloadError: React.FC<{ error: string | null }> = ({ error }) => (
  <div role="status" aria-live="polite">
    {error && (
      <Banner role="status" surface="on-solid" tone="error" title="Template download failed.">
        {error}
      </Banner>
    )}
  </div>
);
